import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocker capture — a private note flagged as a blocker. Writes ONE signal via
 * `signals.create` (`kind:"blocker"`) pinned to the caller-selected destination
 * day (`occurredOn`, a `YYYY-MM-DD` the parent picked from the server's
 * `weekStatus.days`). It creates NO receipt, NO snapshot, NO approval — the
 * signal stays private until the user drafts and approves later.
 */
export function BlockerCapture({
  occurredOn,
  onLogged,
  workspaceTeamId,
}: {
  /** Destination day (YYYY-MM-DD) chosen in the Capture sheet. */
  occurredOn: string;
  onLogged: () => void;
  /**
   * The ACTIVE workspace the capture belongs to. Same contract as typed, voice
   * and photo capture — a blocker must land where the user is looking, not
   * blindly in their primary team. Omitted/null → server keeps its existing
   * `insertSignals` → `resolvePrimaryTeamId` fallback. Membership is enforced
   * server-side by `signals.create` (assertTeamMember); nothing here is a
   * security check.
   */
  workspaceTeamId?: number | null;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [text, setText] = useState("");
  const create = trpc.signals.create.useMutation();
  const trimmed = text.trim();

  const submit = async () => {
    if (!trimmed) return;
    await create.mutateAsync({
      title: trimmed.slice(0, 280),
      kind: "blocker",
      occurredOn,
      ...(trimmed.length > 280 ? { summary: trimmed.slice(0, 2000) } : {}),
      workspaceTeamId,
    });
    setText("");
    toast.success(isAr ? "تم تسجيل المعوق. خاص بك." : "Blocker captured. Private to you.");
    onLogged();
  };

  return (
    <div className="rounded-xl border border-border bg-card/60 p-3" data-testid="blocker-capture">
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 mb-2">
        <AlertTriangle className="h-3.5 w-3.5" /> {isAr ? "ما الذي يعيقك؟" : "What’s blocking you?"}
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={isAr ? "مثال: بانتظار سعة البنية التحتية لإتمام الترحيل" : "e.g. Waiting on infra capacity for the migration"}
        rows={2}
        className="w-full resize-none rounded-lg border border-border bg-background p-2 text-sm outline-none focus:border-coral"
        data-testid="blocker-input"
      />
      <div className="mt-2 flex justify-end">
        <Button
          onClick={submit}
          disabled={!trimmed || create.isPending}
          className="rounded-full"
          size="sm"
        >
          {create.isPending ? "…" : isAr ? "تسجيل المعوق" : "Capture blocker"}
        </Button>
      </div>
    </div>
  );
}
